'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useState } from 'react';
import type { PaymentsResponse } from '@//app/api/commerce/payments/[id]/list';
import { Link } from '@//components/Link';
import { useMarketMetadata } from '@//providers/MarketSettingsProvider';
import { formatCurrency } from '@//utils/currency';
import styles from './AsideActionCard.module.scss';
import FinanceSection from './FinanceSection';

interface PriceListProps {
  itemId: string;
  branchId: string;
  financeSectionId: string;
  insuranceSectionId: string;
  hasLeasingPrice: boolean;
  hasBusinessLeasingPrice: boolean;
  hasPrice: boolean;
  hasInsurance: boolean;
  leasingPrice?: number;
  businessLeasingPrice?: number;
  paymentsResponse?: PaymentsResponse;
}

const PriceList: React.FC<PriceListProps> = ({
  itemId,
  branchId,
  financeSectionId,
  insuranceSectionId,
  hasLeasingPrice,
  hasBusinessLeasingPrice,
  hasPrice,
  hasInsurance,
  leasingPrice,
  businessLeasingPrice,
  paymentsResponse,
}) => {
  const t = useTranslations('pages.item.informationSection');
  const tVehicleGlossary = useTranslations('utils.vehicleGlossary');
  const locale = useLocale();
  const marketMetadata = useMarketMetadata();

  const [financeIsEmpty, setFinanceIsEmpty] = useState(false);

  const handleFinanceEmpty = useCallback(() => {
    setFinanceIsEmpty(true);
  }, []);

  // Don't render ul if only finance would be shown (let FinanceSection handle conditional rendering)
  const hasStaticItems = hasLeasingPrice || hasBusinessLeasingPrice || hasInsurance;

  if (!hasStaticItems && !hasPrice) {
    return null;
  }

  // If only finance section would be shown and it's empty, don't render
  if (!hasStaticItems && financeIsEmpty) {
    return null;
  }

  return (
    <ul className={styles.priceList}>
      {hasLeasingPrice && leasingPrice && (
        <li
          className={styles.priceItem}
          aria-label={t('price.ariaLabel', {
            type: tVehicleGlossary('price.privateLease.label'),
            price: formatCurrency(leasingPrice, {
              locale,
              currency: marketMetadata.currency,
              period: 'month',
            }),
          })}
        >
          <Link href={`#${financeSectionId}`} className={styles.priceLabel}>
            {tVehicleGlossary('price.privateLease.label')}
          </Link>
          <span className={styles.priceValue}>
            {formatCurrency(leasingPrice, {
              locale,
              currency: marketMetadata.currency,
              period: 'month',
            })}
          </span>
        </li>
      )}
      {hasBusinessLeasingPrice && businessLeasingPrice && (
        <li
          className={styles.priceItem}
          aria-label={t('price.ariaLabel', {
            type: tVehicleGlossary('price.businessLease.label'),
            price: formatCurrency(businessLeasingPrice, {
              locale,
              currency: marketMetadata.currency,
              period: 'month',
            }),
          })}
        >
          <Link href={`#${financeSectionId}`} className={styles.priceLabel}>
            {tVehicleGlossary('price.businessLease.label')}
          </Link>
          <span className={styles.priceValue}>
            {formatCurrency(businessLeasingPrice, {
              locale,
              currency: marketMetadata.currency,
              period: 'month',
            })}
          </span>
        </li>
      )}
      {hasPrice && (
        <FinanceSection
          itemId={itemId}
          branchId={branchId}
          financeSectionId={financeSectionId}
          paymentsResponse={paymentsResponse}
          onEmpty={handleFinanceEmpty}
        />
      )}
      {hasInsurance && (
        <li className={styles.priceItem}>
          <Link
            href={`#${insuranceSectionId}`}
            title={t('insurance.title')}
            className={styles.priceLabel}
          >
            {t('insurance.label')}
          </Link>
        </li>
      )}
    </ul>
  );
};

export default PriceList;
